// Rooms + devices for a location — mirrors the Flutter Rooms screen, but shows
// everything on one page: the location's Common Devices, then every Room with
// the devices in it. Rooms come from GetRoomsbyUserIDandLocationId; each room's
// devices from GetDeviceDetailsbyUserIdAndRoomId; common devices from
// GetCommonDevicesbyUserIDandLocationId.

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, AppBar, Toolbar, IconButton, Grid,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  getRooms, getCommonDevices, getDevicesByRoom, getRfDevices,
} from '../../../core/api/api';
import { deviceGridSpan } from '../../../core/devices/deviceMeta';
import { useApp } from '../../../core/state/AppContext';
import DeviceTile from '../components/DeviceTile';
import RfSensorTile from '../../shared/RfSensorTile';
import RfSensorDialog from '../../shared/RfSensorDialog';
import RfSensorMenu from '../../shared/RfSensorMenu';
import { withoutHubs } from '../../../core/devices/hubDevice';
import RoomPresenceBadge from '../../shared/RoomPresenceBadge';
import useRoomPresence from '../../../core/hooks/useRoomPresence';

const asArray = (res) => (res && Array.isArray(res.data) ? res.data : []);

export default function RoomsScreen() {
  const { locationId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { userId } = useApp();
  // The person on an occupied smart room (door + motion sensor).
  const { presenceOf } = useRoomPresence(locationId, { viewerUserId: userId });
  const [groups, setGroups] = useState([]); // ordered: [{ key, name, devices }]
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rooms = asArray(await getRooms(userId, locationId));
        // Common devices + each room's devices, in parallel.
        const [common, ...roomDevs] = await Promise.all([
          getCommonDevices(userId, locationId).then(withoutHubs).catch(() => null),
          ...rooms.map((r) => getDevicesByRoom(userId, r.RoomID, locationId).then(withoutHubs).catch(() => null)),
        ]);

        const next = [];
        const commonDevices = asArray(common);
        if (commonDevices.length) {
          next.push({ key: 'common', name: 'Common Devices', devices: commonDevices });
        }
        rooms.forEach((r, i) => {
          next.push({
            key: `room-${r.RoomID}`,
            roomId: r.RoomID,
            name: r.RoomName || `Room ${r.RoomID}`,
            devices: asArray(roomDevs[i]),
          });
        });
        if (alive) setGroups(next);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [userId, locationId]);

  // ⚠️ AN RF SENSOR IS IN NO DEVICE LIST. A door, safe or fire transmitter has
  // no unique number, so it is not a DevicesInstalled row and no room query
  // returns it — it has to be asked for separately, or the safe locker never
  // appears in the room it is installed in. One call for the location, then
  // split by room. See docs/17.
  const [rfByRoom, setRfByRoom] = useState(new Map());
  const [rfEditing, setRfEditing] = useState(null);
  const [rfMenu, setRfMenu] = useState(null);   // right-click: { device, x, y }
  const loadRf = useCallback(() => {
    if (!locationId) { setRfByRoom(new Map()); return; }
    getRfDevices({ LocationID: locationId, ViewerUserId: userId })
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        const m = new Map();
        rows.filter((d) => String(d.Name || '').trim() && Number(d.Active) === 1)
          .forEach((d) => {
            const k = Number(d.RoomID) || 0;
            if (!m.has(k)) m.set(k, []);
            m.get(k).push(d);
          });
        setRfByRoom(m);
      })
      // An older backend has no such endpoint; the rooms must still draw.
      .catch(() => setRfByRoom(new Map()));
  }, [locationId, userId]);
  useEffect(() => { loadRf(); }, [loadRf]);

  const openDevice = (d) =>
    navigate(`/device/${d.UniqueNumber}`, { state: { device: d } });

  // ⚠️ A WHOLE-LOCATION SENSOR (RoomID 0) BELONGS WITH THE COMMON DEVICES. It was
  // loaded into rfByRoom under 0 and never drawn: the Common group has no roomId,
  // and a location with no common devices had no such group at all. Now it is
  // drawn there, and the group exists whenever either kind does.
  const rfAt = (g) => rfByRoom.get(g.key === 'common' ? 0 : g.roomId) || [];
  const shown = (rfByRoom.get(0) || []).length && !groups.some((g) => g.key === 'common')
    ? [{ key: 'common', name: 'Common Devices', devices: [] }, ...groups]
    : groups;
  const isEmpty = shown.length === 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>
            {state?.name || 'Rooms'}
          </Typography>
          <Box sx={{ width: 40 }} />
        </Toolbar>
      </AppBar>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : isEmpty ? (
        <Typography sx={{ textAlign: 'center', mt: 6 }} color="text.secondary">
          No rooms or devices in this location
        </Typography>
      ) : (
        <Box sx={{ p: 2 }}>
          {shown.map((g) => (
            <Box key={g.key} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <MeetingRoomIcon sx={{ color: 'text.primary' }} fontSize="small" />
                <Typography variant="h6" color="text.primary">{g.name}</Typography>
                {g.roomId != null && <RoomPresenceBadge presence={presenceOf(g.roomId)} tip size="medium" />}
                {g.roomId != null && (
                  <IconButton
                    size="small"
                    sx={{ color: 'primary.main' }}
                    aria-label={`Scenes for ${g.name}`}
                    onClick={() => navigate(`/room/${g.roomId}/scenes`, {
                      // locationId decides whose scene panel is read (the
                      // location owner's), so it must travel with the room.
                      state: { name: g.name, devices: g.devices, locationId },
                    })}
                  >
                    <AutoAwesomeIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {g.devices.length === 0 && !rfAt(g).length ? (
                <Typography variant="body2" color="text.secondary" sx={{ pl: 0.5 }}>
                  No devices in this room
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {g.devices.map((d) => (
                    <Grid item {...deviceGridSpan(d.DeviceID)} key={d.UniqueNumber}>
                      <DeviceTile device={d} onClick={() => openDevice(d)} />
                    </Grid>
                  ))}
                  {/* The room's RF sensors, beside its devices — they read the
                      same to the person looking at them. */}
                  {rfAt(g).map((d) => (
                    <Grid item xs={6} sm={4} md={3} lg={2} key={`rf-${d.RfDeviceId}`}>
                      <RfSensorTile device={d} onOpen={setRfEditing} onMenu={(dev, x, y) => setRfMenu({ device: dev, x, y })} />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          ))}
        </Box>
      )}
      {/* Tapping a sensor configures it where it is. */}
      <RfSensorDialog
        device={rfEditing}
        locationId={locationId}
        onClose={() => setRfEditing(null)}
        onSaved={loadRf}
      />
      <RfSensorMenu
        target={rfMenu}
        onClose={() => setRfMenu(null)}
        onSettings={setRfEditing}
        onChanged={loadRf}
      />
    </Box>
  );
}
